// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService.js';
import employeeService from '../services/employeeService.js';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [employeeType, setEmployeeType] = useState(null);

  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState(null);

  // ==========================================================================
  // EMPLOYEE TYPE
  // ==========================================================================
  //
  // 1 = admin, 2 = customer service. isAdmin() reads it, and it decides which
  // tabs and admin pages appear — so it has to be resolved after every event
  // that can change who the current user is: first load, login, registration,
  // a manual refresh, and both directions of impersonation.
  //
  // That resolution used to be written out at all six of those call sites,
  // fourteen near-identical lines each. Six copies of a permission lookup is
  // six chances for one to drift, and the consequence of drift here is
  // somebody seeing a screen they should not.
  //
  // KNOWN LIMITATION: only companies[0] is consulted. A member belonging to
  // several companies gets their type from whichever comes back first, which
  // may not be the company they are currently working in. featureGate on the
  // server has the same fallback, so at least the two agree — but neither is
  // correct for a multi-company member. Worth fixing together.
  //
  // FAILS CLOSED: any failure returns null, which reads as "not an admin".
  // Wrongly hiding an admin control is a support question; wrongly showing one
  // is a permissions hole.
  const resolveEmployeeType = useCallback(async (user) => {
    if (!user?.isEmployee || !user?.memberId) return null;

    try {
      const companies = await employeeService.getUserCompanies(user.memberId);
      if (!companies || companies.length === 0) return null;

      const employees = await employeeService.getCompanyEmployees(companies[0].companyId);
      const currentEmployee = employees.find(
        (emp) => emp.memberEmployeeId === user.memberId
      );

      return currentEmployee ? currentEmployee.employeeTypeId : null;
    } catch (error) {
      console.error('[auth] Could not resolve employee type:', error);
      return null;
    }
  }, []);

  // Everything a sign-out, a failed load, or a lost session has to undo.
  // Written once so no path can forget one of them — a stale employeeType
  // after logout would leave admin controls on screen for the next user.
  const clearAuthState = useCallback(() => {
    setCurrentUser(null);
    setIsAuthenticated(false);
    setEmployeeType(null);
    setIsImpersonating(false);
    setOriginalUser(null);
  }, []);

  const applyImpersonationState = useCallback((user) => {
    if (user?.isImpersonating && user?.originalUser) {
      setIsImpersonating(true);
      setOriginalUser(user.originalUser);
    } else {
      setIsImpersonating(false);
      setOriginalUser(null);
    }
  }, []);

  // ==========================================================================
  // INITIAL LOAD
  // ==========================================================================

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const user = await authService.getCurrentUser();

        setCurrentUser(user);
        setIsAuthenticated(true);
        applyImpersonationState(user);
        setEmployeeType(await resolveEmployeeType(user));
      } catch (error) {
        // Expected on every anonymous page load — there is no session to
        // fetch. Not logged, because it is the normal case rather than a
        // fault, and logging it would cry wolf on every visit.
        clearAuthState();
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, [resolveEmployeeType, clearAuthState, applyImpersonationState]);

  // ==========================================================================
  // AUTH ACTIONS
  // ==========================================================================

  const login = useCallback(async (email, password) => {
    const response = await authService.login(email, password);

    setCurrentUser(response.user);
    setIsAuthenticated(true);
    setIsImpersonating(false);
    setOriginalUser(null);
    setEmployeeType(await resolveEmployeeType(response.user));

    return response.user;
  }, [resolveEmployeeType]);

  const register = useCallback(async (userData) => {
    const response = await authService.register(userData);

    // A token only comes back when autoLogin was requested. Without one the
    // account exists but the session does not, and the caller drives the next
    // step — email verification during registration, for instance.
    if (response.token) {
      setCurrentUser(response.user);
      setIsAuthenticated(true);
      setIsImpersonating(false);
      setOriginalUser(null);
      setEmployeeType(await resolveEmployeeType(response.user));
    }

    return response;
  }, [resolveEmployeeType]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Clear the client either way. A server that cannot process the logout
      // must not leave someone appearing signed in on a shared machine.
      console.error('[auth] Logout request failed, clearing client state anyway:', error);
    } finally {
      clearAuthState();
    }
  }, [clearAuthState]);

  const updateLogoutSettings = useCallback(
    async (automaticLogoutDurationMinutes, lessThanDayLogout) => {
      const response = await authService.updateLogoutSettings(
        automaticLogoutDurationMinutes,
        lessThanDayLogout
      );

      setCurrentUser((prevUser) => ({
        ...prevUser,
        automaticLogoutDurationMinutes: response.automaticLogoutDurationMinutes,
        lessThanDayLogout: response.lessThanDayLogout
      }));

      return response;
    },
    []
  );

  const refreshUser = useCallback(async () => {
    try {
      const user = await authService.getCurrentUser();

      setCurrentUser(user);
      setIsAuthenticated(true);
      applyImpersonationState(user);
      setEmployeeType(await resolveEmployeeType(user));

      return user;
    } catch (error) {
      // Unlike the initial load, this one IS a fault: something called refresh
      // while expecting a live session, and there is not one.
      console.error('[auth] Failed to refresh user:', error);
      clearAuthState();
      throw error;
    }
  }, [resolveEmployeeType, clearAuthState, applyImpersonationState]);

  const deletePersonalAccount = useCallback(async (email) => {
    const response = await authService.deletePersonalAccount(email);
    clearAuthState();
    return response;
  }, [clearAuthState]);

  // ==========================================================================
  // IMPERSONATION — super admin only
  // ==========================================================================
  //
  // Both directions replace the current user entirely, so employeeType has to
  // be resolved again. Carrying the admin's type into an impersonated session
  // would show them controls the person they are impersonating cannot use,
  // which defeats the purpose of looking through their eyes.

  const startImpersonation = useCallback(async (memberId) => {
    const response = await authService.startImpersonation(memberId);

    setCurrentUser(response.user);
    setIsImpersonating(true);
    setOriginalUser(response.user.originalUser);
    setEmployeeType(await resolveEmployeeType(response.user));

    return response;
  }, [resolveEmployeeType]);

  const stopImpersonation = useCallback(async () => {
    const response = await authService.stopImpersonation();

    setCurrentUser(response.user);
    setIsImpersonating(false);
    setOriginalUser(null);
    setEmployeeType(await resolveEmployeeType(response.user));

    return response;
  }, [resolveEmployeeType]);

  const searchMembersForImpersonation = useCallback(async (searchQuery) => {
    return authService.searchMembersForImpersonation(searchQuery);
  }, []);

  // ==========================================================================
  // ROLE HELPERS
  // ==========================================================================
  //
  // isAdmin matches the server exactly: subscriptions.js checkAdminAccess
  // rejects anything where employeetypeid !== 1, and promos.js
  // requireBillingAdmin does the same. If this ever diverges, the UI will
  // offer actions the API refuses.

  const isAdmin = useCallback(() => employeeType === 1, [employeeType]);

  const isCustomerService = useCallback(() => employeeType === 2, [employeeType]);

  const isSuperAdmin = useCallback(
    () => currentUser?.isSuperAdmin || false,
    [currentUser]
  );

  const value = {
    currentUser,
    isAuthenticated,
    loading,
    employeeType,
    isAdmin,
    isCustomerService,
    isSuperAdmin,
    login,
    register,
    logout,
    updateLogoutSettings,
    refreshUser,
    deletePersonalAccount,
    // Impersonation
    isImpersonating,
    originalUser,
    startImpersonation,
    stopImpersonation,
    searchMembersForImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};